import React, { useState, useEffect, useCallback } from 'react';
import {
  Row,
  Col,
  Form,
  Card,
  Input,
  Space,
  Select,
  message,
  DatePicker,
  Radio,
  Spin,
  Tag
} from 'antd';
import { PlusCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import FormItem from '@/common/FormItem';
import styles from '@/common/Layout/index.module.less';
import CustomButton from '@/common/CustomButton';
import { history } from 'app/history';
import Layout from '@/common/Layout';
import { v4 as uuidv4 } from 'uuid';
import {
  useGetUacrOfSamplingOrDbarAreaMastersQuery,
  useInitiateVerificationMutation,
  useCreateUacrOfSamplingOrDbarMutation,
  useSaveUacrDraftMutation,
  useGetUacrOfSamplingOrDbarsQuery,
  useLazyGetUacrAvailableBatchesQuery
} from '../../redux/apiSlice';
import moment from 'moment';
import CustomModal from '../../../../common/CustomModal';
import { useLocation } from 'react-router-dom';

import LocalStorage from 'utilities/localStorage';
const user = LocalStorage.getItem('Gravity_user');

const typeOfCleaningOptions = [
  { id: 1, label: 'TYPE A', value: 'TYPE A' },
  { id: 2, label: 'TYPE C', value: 'TYPE C' },
  { id: 3, label: 'NA', value: 'NA' }
];

const cleaningAccessoriesOptions = [
  { label: 'YES', value: 'YES' },
  { label: 'N/A', value: 'N/A' }
];

const toDateString = (val) => {
  if (!val) return null;
  if (val === 'N/A') return 'N/A';
  if (moment.isMoment(val)) return val.toISOString();
  return val;
};

const createProductNewPage = () => {
  const [form] = Form.useForm();
  const [form2] = Form.useForm();

  const searchID = useLocation().search;
  const id = new URLSearchParams(searchID).get('id');

  const [search, setSearch] = useState('');
  const [isModal, setIsModal] = useState(false);
  const [verifyingActivityIndex, setVerifyingActivityIndex] = useState(null);
  // Parent id once a draft has been persisted (lets subsequent saves + the final
  // submit update instead of insert). Seeded from the URL id when resuming a draft.
  const [draftId, setDraftId] = useState(id || null);
  const [savingDraftIndex, setSavingDraftIndex] = useState(null);
  const [originalLafOffTime, setOriginalLafOffTime] = useState('');
  const [messageApi, contextHolder] = message.useMessage();
  const [selectedProductNames, setSelectedProductNames] = useState([]);
  const [lafOnTimeOptions, setLafOnTimeOptions] = useState([]);
  // Per-activity NA toggle flags. Key: `${activityIndex}_${fieldKey}`
  const [naFlags, setNaFlags] = useState({});

  const isNAField = (idx, field) => !!naFlags[`${idx}_${field}`];
  const setNAField = (idx, field, checked) => {
    const k = `${idx}_${field}`;
    setNaFlags((prev) => ({ ...prev, [k]: checked }));
    form.setFields([
      {
        name: ['cleaning_activities', idx, field],
        value: checked ? 'NA' : null
      }
    ]);
  };

  const [activityStates, setActivityStates] = useState([
    { isNew: true, isVerified: false, uniqueId: uuidv4() }
  ]);

  const handleShowMessage = (type, text) => {
    messageApi.open({ type, content: text });
  };

  // #region API hooks
  let { data: areaMasters, refetch: refetchAreas } =
    useGetUacrOfSamplingOrDbarAreaMastersQuery({ search });

  const { data, isLoading: isLoadingEdit } = useGetUacrOfSamplingOrDbarsQuery({
    page: 1,
    per_page: '',
    from_date: '',
    to_date: '',
    id: id
  });

  const [createUacrOfSamplingOrDbar, { isLoading: isSaving }] =
    useCreateUacrOfSamplingOrDbarMutation();

  const [initiateVerification, { isLoading: isVerifying }] =
    useInitiateVerificationMutation();

  const [saveUacrDraft] = useSaveUacrDraftMutation();

  const [triggerBatchLookup, { isFetching: isBatchFetching }] =
    useLazyGetUacrAvailableBatchesQuery();
  const [availableBatches, setAvailableBatches] = useState([]);
  // #endregion

  // #region Computed
  const anyVerified = activityStates.some((s) => s.isVerified || !s.isNew);
  const canSubmit = activityStates.every((s) => !s.isNew || s.isVerified);
  // #endregion

  // #region Verification modal
  const handleVerifyActivity = async (activityIndex) => {
    try {
      await form.validateFields([
        'samplingDispensingBoothAreaMasterId',
        ['cleaning_activities', activityIndex, 'activity_start_time_date'],
        ['cleaning_activities', activityIndex, 'activity_end_time_date'],
        ['cleaning_activities', activityIndex, 'type_of_cleaning'],
        ['cleaning_activities', activityIndex, 'start_date'],
        ['cleaning_activities', activityIndex, 'end_date'],
        ['cleaning_activities', activityIndex, 'cleaning_accessories'],
        ['cleaning_activities', activityIndex, 'remarks']
      ]);
      setVerifyingActivityIndex(activityIndex);
      setIsModal(true);
    } catch (e) {
      console.log('Validation failed:', e);
    }
  };

  const handleCloseModal = () => {
    setIsModal(false);
    setVerifyingActivityIndex(null);
  };

  const onFinishModal = async () => {
    try {
      const values = await form2.validateFields();
      const idx = verifyingActivityIndex;
      const uid = activityStates[idx]?.uniqueId;

      const response = await initiateVerification({
        ...values,
        common_unique_id: uid,
        form: 'usage_cleaning'
      });

      if (response?.data?.success === true) {
        form.setFields([
          {
            name: ['cleaning_activities', idx, 'verified_by'],
            value: values.email
          },
          {
            name: ['cleaning_activities', idx, 'verified_by_and_date_display'],
            value: `${values.email} (${moment().format('DD-MM-YY')})`
          },
          {
            name: ['cleaning_activities', idx, 'remarks'],
            value:
              form.getFieldValue(['cleaning_activities', idx, 'remarks']) ||
              values.remarks2 ||
              ''
          }
        ]);

        setActivityStates((prev) =>
          prev.map((s, i) => (i === idx ? { ...s, isVerified: true } : s))
        );
        form2.resetFields();
        setIsModal(false);
        setVerifyingActivityIndex(null);
        handleShowMessage('success', 'Verification successful');
      } else {
        handleShowMessage(
          'error',
          response?.error?.data?.message || 'Verification failed'
        );
      }
    } catch (e) {
      console.log('Verification failed:', e);
    }
  };
  // #endregion

  // #region Activity count change handler
  const handleActivityCountChange = useCallback(
    (action, totalAfterChange) => {
      if (action === 'add') {
        // When going from 1→2, save the original LAF Off of card 0
        if (totalAfterChange === 2) {
          const currentLafOff = form.getFieldValue([
            'cleaning_activities',
            0,
            'laf_off_time'
          ]);
          if (currentLafOff && currentLafOff !== 'N/A') {
            setOriginalLafOffTime(currentLafOff);
          }
        }
        // Set the second-to-last activity's LAF Off to N/A (it's no longer the last)
        const prevLastIdx = totalAfterChange - 2;
        if (prevLastIdx >= 0) {
          form.setFields([
            {
              name: ['cleaning_activities', prevLastIdx, 'laf_off_time'],
              value: 'N/A'
            }
          ]);
        }
      } else if (action === 'remove' && totalAfterChange === 1) {
        // Restoring back to single activity — restore original LAF Off
        form.setFields([
          {
            name: ['cleaning_activities', 0, 'laf_off_time'],
            value:
              originalLafOffTime && moment.isMoment(originalLafOffTime)
                ? originalLafOffTime
                : originalLafOffTime
                ? moment(originalLafOffTime)
                : null
          }
        ]);
      }
    },
    [form, originalLafOffTime]
  );
  // #endregion

  // #region Edit mode populate
  useEffect(() => {
    if (id && data?.data) {
      const record = data.data;
      // Split batch_no string back into array for multi-select
      const batchNos = record.laf_product_batch_no
        ? record.laf_product_batch_no
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        : [];
      // Populate availableBatches so multi-select options render
      setAvailableBatches(
        batchNos.map((bno) => ({
          batch_no: bno,
          product_name: record.laf_product_name || ''
        }))
      );
      setSelectedProductNames(
        record.laf_product_name
          ? [
              ...new Set(
                record.laf_product_name
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean)
              )
            ]
          : []
      );
      form.setFieldsValue({
        samplingDispensingBoothAreaMasterId:
          record.samplingDispensingBoothAreaMasterId,
        laf_product_name: record.laf_product_name,
        laf_product_batch_no: batchNos
      });

      const activityLogs = record.uacr_cleaning_activity_logs;
      if (activityLogs && activityLogs.length > 0) {
        // Populate LAF On Time dropdown with the saved value for first activity
        const firstLafOn = activityLogs[0]?.laf_on_time;
        if (firstLafOn && firstLafOn !== 'N/A') {
          setLafOnTimeOptions([{ value: firstLafOn, label: firstLafOn }]);
        }
        // A loaded card is locked only if it was already verified. Unverified
        // (draft) cards stay editable so the user can finish + verify them.
        setActivityStates(
          activityLogs.map((log) => ({
            isNew: !log.verified_by,
            isVerified: !!log.verified_by,
            uniqueId: uuidv4()
          }))
        );

        // Seed NA toggle flags from saved values
        const savedNaFlags = {};
        activityLogs.forEach((log, idx) => {
          if (log.activity_start_time_is_na)
            savedNaFlags[`${idx}_activity_start_time_date`] = true;
          if (log.activity_end_time_is_na)
            savedNaFlags[`${idx}_activity_end_time_date`] = true;
          if (log.laf_off_time === 'NA')
            savedNaFlags[`${idx}_laf_off_time`] = true;
        });
        setNaFlags(savedNaFlags);

        form.setFieldsValue({
          cleaning_activities: activityLogs.map((log) => ({
            id: log.id,
            laf_on_time: log.laf_on_time || '',
            activity_start_time_date: log.activity_start_time_is_na
              ? 'NA'
              : log.activity_start_time_date
              ? moment(log.activity_start_time_date)
              : null,
            activity_start_time_user_id: log.activity_start_time_user_id,
            activity_end_time_date: log.activity_end_time_is_na
              ? 'NA'
              : log.activity_end_time_date
              ? moment(log.activity_end_time_date)
              : null,
            activity_end_time_user_id: log.activity_end_time_user_id,
            // laf_off_time: log.laf_off_time || '',
            laf_off_time:
              log.laf_off_time === 'NA'
                ? 'NA'
                : log.laf_off_time
                ? moment(log.laf_off_time)
                : null,
            type_of_cleaning: log.type_of_cleaning,
            start_date: log.start_date ? moment(log.start_date) : null,
            end_date: log.end_date ? moment(log.end_date) : null,
            cleaning_accessories: log.cleaning_accessories || 'N/A',
            done_by_user_id: log.done_by,
            done_by_user_id_and_date_display: log.done_by
              ? `${log.done_by} (${moment(log.done_by_date).format(
                  'DD-MM-YY'
                )})`
              : '',
            verified_by: log.verified_by,
            verified_by_and_date_display: log.verified_by
              ? `${log.verified_by} (${moment(log.verified_by_date).format(
                  'DD-MM-YY'
                )})`
              : '',
            remarks: log.remarks || ''
          }))
        });

        if (activityLogs.length === 1) {
          // setOriginalLafOffTime(activityLogs[0].laf_off_time || '');
          setOriginalLafOffTime(
            activityLogs[0].laf_off_time
              ? moment(activityLogs[0].laf_off_time)
              : ''
          );
        }
      } else {
        // Legacy
        setActivityStates([
          { isNew: false, isVerified: true, uniqueId: uuidv4() }
        ]);
        form.setFieldsValue({
          cleaning_activities: [
            {
              laf_on_time: record.laf_time || '',
              activity_start_time_date: record.activity_start_time_date
                ? moment(record.activity_start_time_date)
                : null,
              activity_start_time_user_id: record.activity_start_time_user_id,
              activity_end_time_date: record.activity_end_time_date
                ? moment(record.activity_end_time_date)
                : null,
              activity_end_time_user_id: record.activity_end_time_user_id,
              // laf_off_time: record.laf_off_time || '',
              laf_off_time: record.laf_off_time
                ? moment(record.laf_off_time)
                : null,
              type_of_cleaning: record.type_of_cleaning,
              start_date: record.start_date ? moment(record.start_date) : null,
              end_date: record.end_date ? moment(record.end_date) : null,
              cleaning_accessories: 'YES',
              done_by_user_id: record.done_by,
              done_by_user_id_and_date_display: record.done_by
                ? `${record.done_by} (${moment(record.done_by_date).format(
                    'DD-MM-YY'
                  )})`
                : '',
              verified_by: record.verified_by,
              verified_by_and_date_display: record.verified_by
                ? `${record.verified_by} (${moment(
                    record.verified_by_date
                  ).format('DD-MM-YY')})`
                : '',
              remarks: record.remarks || ''
            }
          ]
        });
        // setOriginalLafOffTime(record.laf_off_time || '');
        setOriginalLafOffTime(
          record.laf_off_time ? moment(record.laf_off_time) : ''
        );
      }
    }
  }, [data]);
  // #endregion

  // #region New activity defaults
  const getNewActivityDefaults = () => {
    const activities = form.getFieldValue('cleaning_activities') || [];
    const lastActivity = activities[activities.length - 1];
    const prevLafOff = lastActivity?.laf_off_time;

    const lafOnTime =
      prevLafOff && prevLafOff !== 'N/A' && moment.isMoment(prevLafOff)
        ? prevLafOff.format('DD-MM-YYYY HH:mm:ss')
        : prevLafOff || 'N/A';

    return {
      laf_on_time: lafOnTime,
      activity_start_time_date: null,
      activity_start_time_user_id: user?.user?.employee,
      activity_end_time_date: null,
      activity_end_time_user_id: user?.user?.employee,
      laf_off_time: null,
      type_of_cleaning: undefined,
      start_date: null,
      end_date: null,
      cleaning_accessories: undefined,
      done_by_user_id: user?.user?.employee,
      done_by_user_id_and_date_display: `${
        user?.user?.employee
      } (${moment().format('DD-MM-YY')})`,
      verified_by: '',
      verified_by_and_date_display: '',
      remarks: ''
    };
  };
  // #endregion

  // #region Save draft (partial, unverified)
  // Persists the current form as-is (no validation) so the user can finish a card
  // later. The whole record is saved; the triggering card gets the "Draft saved"
  // stamp. The returned parent/card ids are mapped back so the next save and the
  // final verified submit update the same rows instead of creating duplicates.
  const handleSaveDraft = async (activityIndex) => {
    const values = form.getFieldsValue();
    const payload = {
      samplingDispensingBoothAreaMasterId:
        values.samplingDispensingBoothAreaMasterId,
      laf_date: null,
      laf_product_name: values.laf_product_name,
      laf_product_batch_no: Array.isArray(values.laf_product_batch_no)
        ? values.laf_product_batch_no.join(', ')
        : values.laf_product_batch_no,
      cleaning_activities: (values.cleaning_activities || []).map(
        (act, idx) => ({
          id: act.id || null,
          activity_order: idx + 1,
          activity_start_time_user_id:
            act.activity_start_time_user_id || user?.user?.employee,
          activity_start_time_date: isNAField(idx, 'activity_start_time_date')
            ? null
            : toDateString(act.activity_start_time_date),
          activity_start_time_is_na: isNAField(idx, 'activity_start_time_date'),
          activity_end_time_user_id:
            act.activity_end_time_user_id || user?.user?.employee,
          activity_end_time_date: isNAField(idx, 'activity_end_time_date')
            ? null
            : toDateString(act.activity_end_time_date),
          activity_end_time_is_na: isNAField(idx, 'activity_end_time_date'),
          laf_on_time: toDateString(act.laf_on_time),
          laf_off_time: toDateString(act.laf_off_time),
          type_of_cleaning: act.type_of_cleaning,
          start_date: toDateString(act.start_date),
          end_date: toDateString(act.end_date),
          cleaningAccessoriesMasterId: [],
          cleaning_accessories: act.cleaning_accessories,
          verified_by: act.verified_by,
          done_by_date: toDateString(act.done_by_date),
          remarks: act.remarks
        })
      )
    };
    if (draftId) payload.id = draftId;

    try {
      setSavingDraftIndex(activityIndex);
      const response = await saveUacrDraft(payload);
      if (response?.data?.success === true) {
        const savedId = response.data.data?.id;
        const savedActivities = response.data.data?.activities || [];
        if (savedId) setDraftId(savedId);
        // Map the persisted activity ids back into the hidden id field by order
        savedActivities.forEach((sa) => {
          form.setFields([
            {
              name: ['cleaning_activities', sa.activity_order - 1, 'id'],
              value: sa.id
            }
          ]);
        });
        // Stamp the card the user saved from
        setActivityStates((prev) =>
          prev.map((s, i) =>
            i === activityIndex
              ? { ...s, draftSavedAt: moment().format('DD-MM-YY HH:mm') }
              : s
          )
        );
        handleShowMessage('success', 'Draft saved');
      } else {
        handleShowMessage(
          'error',
          response?.error?.data?.message || 'Failed to save draft'
        );
      }
    } catch (e) {
      console.log('Save draft failed:', e);
      handleShowMessage('error', 'Failed to save draft');
    } finally {
      setSavingDraftIndex(null);
    }
  };
  // #endregion

  // #region Form submission
  const onFinish = async (values) => {
    const firstVerifiedNew = activityStates.find(
      (s) => s.isNew && s.isVerified
    );
    const commonUniqueId =
      firstVerifiedNew?.uniqueId || activityStates[0]?.uniqueId;

    const payload = {
      samplingDispensingBoothAreaMasterId:
        values.samplingDispensingBoothAreaMasterId,
      laf_date: null,
      laf_product_name: values.laf_product_name,
      laf_product_batch_no: Array.isArray(values.laf_product_batch_no)
        ? values.laf_product_batch_no.join(', ')
        : values.laf_product_batch_no,
      common_unique_id: commonUniqueId,
      cleaning_activities: (values.cleaning_activities || []).map(
        (act, idx) => ({
          id: act.id || null,
          activity_order: idx + 1,
          activity_start_time_user_id:
            act.activity_start_time_user_id || user?.user?.employee,
          activity_start_time_date: isNAField(idx, 'activity_start_time_date')
            ? null
            : toDateString(act.activity_start_time_date),
          activity_start_time_is_na: isNAField(idx, 'activity_start_time_date'),
          activity_end_time_user_id:
            act.activity_end_time_user_id || user?.user?.employee,
          activity_end_time_date: isNAField(idx, 'activity_end_time_date')
            ? null
            : toDateString(act.activity_end_time_date),
          activity_end_time_is_na: isNAField(idx, 'activity_end_time_date'),
          laf_on_time: toDateString(act.laf_on_time),
          laf_off_time: toDateString(act.laf_off_time),
          type_of_cleaning: act.type_of_cleaning,
          start_date: toDateString(act.start_date),
          end_date: toDateString(act.end_date),
          cleaningAccessoriesMasterId: [],
          cleaning_accessories: act.cleaning_accessories,
          verified_by: act.verified_by,
          done_by_date: toDateString(act.done_by_date),
          remarks: act.remarks
        })
      )
    };
    // Finalize against an existing record id (edit) or a draft persisted this
    // session so the verified submit updates instead of inserting a duplicate.
    if (id || draftId) payload.id = id || draftId;

    const response = await createUacrOfSamplingOrDbar(payload);
    if (response?.data?.success === true) {
      handleShowMessage('success', response?.data?.message);
      setTimeout(() => history.push('/uacr-of-sampling-or-dbar-list'), 1500);
    } else {
      handleShowMessage(
        'error',
        response?.error?.data?.message || 'Something went wrong'
      );
    }
  };
  // #endregion

  // #region Area onChange — fetch available batches
  const onChange = async (e, name) => {
    if (name === 'area_name') {
      // Set user_id for first activity
      form.setFields([
        {
          name: ['cleaning_activities', 0, 'activity_start_time_user_id'],
          value: user?.user?.employee
        },
        {
          name: ['cleaning_activities', 0, 'activity_end_time_user_id'],
          value: user?.user?.employee
        }
      ]);

      // Reset batch fields
      form.setFieldsValue({
        laf_product_batch_no: undefined,
        laf_product_name: ''
      });
      setAvailableBatches([]);
      setSelectedProductNames([]);

      // Fetch available batches for the selected area
      const areaObj = areaMasters?.data?.find((a) => a.id == e);
      if (areaObj?.area) {
        try {
          const result = await triggerBatchLookup({
            area: areaObj.area
          }).unwrap();
          if (result?.success && result?.data) {
            setAvailableBatches(result.data);
            // Reset LAF ON Time — user will pick from dropdown
            form.setFields([
              {
                name: ['cleaning_activities', 0, 'laf_on_time'],
                value: undefined
              }
            ]);
          }
        } catch (err) {
          console.log(err);
          handleShowMessage('error', 'Failed to fetch available batches');
        }
      }
    }
  };

  // When batch selection changes, update product names display + hidden field
  const onBatchChange = (selectedBatchNos) => {
    if (!selectedBatchNos || selectedBatchNos.length === 0) {
      setSelectedProductNames([]);
      form.setFieldsValue({ laf_product_name: '' });
      // Reset LAF ON Time if no batches selected
      setLafOnTimeOptions([]);
      form.setFields([
        {
          name: ['cleaning_activities', 0, 'laf_on_time'],
          value: undefined
        }
      ]);
      return;
    }
    const names = selectedBatchNos
      .map((bno) => {
        const match = availableBatches.find((b) => b.batch_no === bno);
        return match?.product_name || '';
      })
      .filter(Boolean);
    const unique = [...new Set(names)];
    setSelectedProductNames(unique);
    form.setFieldsValue({ laf_product_name: unique.join(', ') });

    // Build LAF ON Time dropdown options from selected batches
    const opts = availableBatches
      .filter((b) => selectedBatchNos.includes(b.batch_no) && b.laf_time_date)
      .map((b) => ({
        value: moment(b.laf_time_date).format('DD-MM-YYYY HH:mm:ss'),
        label: `${moment(b.laf_time_date).format('DD-MM-YYYY HH:mm:ss')} (${
          b.batch_no
        })`
      }));
    setLafOnTimeOptions(opts);
  };

  const onSearch = (e, name) => {
    if (e.length === 0 || e.length >= 3) {
      if (name === 'area_name') {
        setSearch(e);
        refetchAreas();
      }
    }
  };
  // #endregion

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {contextHolder}
      <Layout sider={false}>
        <Spin spinning={!!id && isLoadingEdit}>
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{
              cleaning_activities: [
                {
                  laf_on_time: '',
                  laf_off_time: '',
                  activity_start_time_user_id: user?.user?.employee,
                  activity_start_time_date: null,
                  activity_end_time_user_id: user?.user?.employee,
                  activity_end_time_date: null,
                  done_by_user_id: user?.user?.employee,
                  done_by_user_id_and_date_display: `${
                    user?.user?.employee
                  } (${moment().format('DD-MM-YY')})`,
                  verified_by: '',
                  verified_by_and_date_display: '',
                  remarks: ''
                }
              ]
            }}
          >
            {/* ========== HEADER CARD ========== */}
            <Card
              title="USAGE AND CLEANING RECORD OF SAMPLING / DISPENSING BOOTH AND ROOM"
              className={styles.CustomPanel}
            >
              <Row gutter={24}>
                <Col xs={24} xl={12} span={12} md={24} sm={24}>
                  <FormItem
                    label="Area (Equipment ID)"
                    name="samplingDispensingBoothAreaMasterId"
                    rules={[
                      { required: true, message: 'Select Area (Equipment ID)' }
                    ]}
                    className={styles.customLable}
                  >
                    <Select
                      placeholder="Select Area (Equipment ID)"
                      showSearch
                      optionFilterProp="children"
                      onSearch={(e) => onSearch(e, 'area_name')}
                      onChange={(e) => onChange(e, 'area_name')}
                      disabled={anyVerified}
                    >
                      {areaMasters?.data?.map((item, index) => (
                        <Select.Option value={item.id} key={index}>
                          {item.description
                            ? `${item.description} (${item.area})`
                            : item.area}
                        </Select.Option>
                      ))}
                    </Select>
                  </FormItem>
                </Col>
                <Col xs={24} xl={12} span={24} md={24} sm={24}>
                  <FormItem
                    label="Product Batch No. / In-House Batch No."
                    name="laf_product_batch_no"
                    rules={[
                      {
                        required: true,
                        message: 'Select Product Batch No.'
                      }
                    ]}
                    className={styles.customLable}
                  >
                    <Select
                      mode="multiple"
                      placeholder={
                        isBatchFetching
                          ? 'Loading batches...'
                          : 'Select Product Batch No.'
                      }
                      loading={isBatchFetching}
                      onChange={onBatchChange}
                      disabled={anyVerified}
                      style={{ width: '100%' }}
                      optionFilterProp="children"
                      showSearch
                    >
                      <Select.Option value="NA" key="NA">
                        NA
                      </Select.Option>
                      {availableBatches.map((b, idx) => {
                        const suffix =
                          b.process_label_name || b.sub_process_name
                            ? ` (${b.process_label_name || 'NA'}${
                                b.sub_process_name
                                  ? ` › ${b.sub_process_name}`
                                  : ''
                              })`
                            : '';
                        return (
                          <Select.Option value={b.batch_no} key={idx}>
                            {`${b.batch_no}${suffix}`}
                          </Select.Option>
                        );
                      })}
                    </Select>
                  </FormItem>
                </Col>
                <Col xs={24} xl={24} span={24} md={24} sm={24}>
                  <FormItem
                    label="Product / Material Name"
                    className={styles.customLable}
                  >
                    <div
                      style={{
                        minHeight: 40,
                        padding: '4px 8px',
                        border: '1px solid #d9d9d9',
                        borderRadius: 4,
                        background: '#f5f5f5',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 6,
                        alignItems: 'center'
                      }}
                    >
                      {selectedProductNames.length === 0 ? (
                        <span style={{ color: '#bfbfbf' }}>
                          Select batch numbers to see product names
                        </span>
                      ) : (
                        selectedProductNames.map((name, idx) => (
                          <span
                            key={idx}
                            style={{
                              background: '#e6f7ff',
                              border: '1px solid #91d5ff',
                              borderRadius: 4,
                              padding: '2px 8px',
                              fontSize: 13
                            }}
                          >
                            {name}
                          </span>
                        ))
                      )}
                    </div>
                  </FormItem>
                  {/* Hidden field to store product name for payload */}
                  <FormItem name="laf_product_name" hidden>
                    <Input />
                  </FormItem>
                </Col>
              </Row>
            </Card>

            <br />

            {/* ========== DYNAMIC CLEANING ACTIVITY CARDS ========== */}
            <Form.List name="cleaning_activities">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }, index) => {
                    const activities =
                      form.getFieldValue('cleaning_activities') || [];
                    const total = activities.length;
                    const isFirstCard = index === 0;
                    const state = activityStates[index] || {
                      isNew: true,
                      isVerified: false
                    };

                    const fieldsLocked = state.isNew && state.isVerified;
                    const showVerifyButton = state.isNew && !state.isVerified;
                    const showDeleteButton =
                      !isFirstCard && state.isNew && !state.isVerified;

                    return (
                      <div key={key}>
                        <Card
                          title={`Activity Log ${index + 1}`}
                          className={styles.CustomPanel}
                          extra={
                            <Space>
                              {showVerifyButton && state.draftSavedAt ? (
                                <Tag color="gold">
                                  Draft saved · {state.draftSavedAt}
                                </Tag>
                              ) : null}
                              {showDeleteButton ? (
                                <DeleteOutlined
                                  style={{ color: 'red', fontSize: '16px' }}
                                  onClick={() => {
                                    setActivityStates((prev) => {
                                      const n = [...prev];
                                      n.splice(index, 1);
                                      return n;
                                    });
                                    handleActivityCountChange(
                                      'remove',
                                      total - 1
                                    );
                                    remove(name);
                                  }}
                                />
                              ) : null}
                            </Space>
                          }
                        >
                          <Row gutter={24}>
                            {/* A. LAF On Time */}
                            <Col xs={24} xl={12} span={24} md={24} sm={24}>
                              {isFirstCard ? (
                                <FormItem
                                  {...restField}
                                  label="LAF On Time"
                                  name={[name, 'laf_on_time']}
                                  rules={[
                                    {
                                      required: true,
                                      message: 'Select LAF On Time'
                                    }
                                  ]}
                                  className={styles.customLable}
                                >
                                  <Select
                                    placeholder="Select LAF On Time"
                                    disabled={fieldsLocked}
                                    style={{ width: '100%' }}
                                    allowClear
                                  >
                                    <Select.Option value="NA" key="NA">
                                      NA
                                    </Select.Option>
                                    {lafOnTimeOptions.map((opt, idx) => (
                                      <Select.Option
                                        value={opt.value}
                                        key={idx}
                                      >
                                        {opt.label}
                                      </Select.Option>
                                    ))}
                                  </Select>
                                </FormItem>
                              ) : (
                                <FormItem
                                  {...restField}
                                  label="LAF On Time"
                                  name={[name, 'laf_on_time']}
                                  className={styles.customLable}
                                >
                                  <Input
                                    disabled
                                    style={{ width: '100%' }}
                                    placeholder="N/A"
                                  />
                                </FormItem>
                              )}
                            </Col>

                            {/* B. Activity Start Date & Time (DatePicker or NA, mandatory) */}
                            <Col
                              xs={24}
                              xl={12}
                              span={24}
                              md={24}
                              sm={24}
                              style={{ position: 'relative' }}
                            >
                              <Radio.Group
                                size="small"
                                buttonStyle="solid"
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  right: 12,
                                  zIndex: 1
                                }}
                                value={
                                  isNAField(index, 'activity_start_time_date')
                                    ? 'na'
                                    : 'date'
                                }
                                disabled={fieldsLocked}
                                onChange={(e) =>
                                  setNAField(
                                    index,
                                    'activity_start_time_date',
                                    e.target.value === 'na'
                                  )
                                }
                              >
                                <Radio.Button value="date">Date</Radio.Button>
                                <Radio.Button value="na">NA</Radio.Button>
                              </Radio.Group>
                              <FormItem
                                {...restField}
                                label="Activity Start Date & Time"
                                name={[name, 'activity_start_time_date']}
                                rules={[
                                  {
                                    required: true,
                                    message: 'Select Activity Start Date & Time'
                                  }
                                ]}
                                className={styles.customLable}
                              >
                                {isNAField(
                                  index,
                                  'activity_start_time_date'
                                ) ? (
                                  <Input
                                    disabled
                                    style={{ width: '100%' }}
                                    placeholder="NA"
                                  />
                                ) : (
                                  <DatePicker
                                    showTime
                                    disabled={fieldsLocked}
                                    style={{ width: '100%' }}
                                    placeholder="Select Activity Start Date & Time"
                                  />
                                )}
                              </FormItem>
                            </Col>
                            {/* Hidden: user_id for activity start */}
                            <Col hidden>
                              <FormItem
                                {...restField}
                                name={[name, 'activity_start_time_user_id']}
                              >
                                <Input />
                              </FormItem>
                            </Col>

                            {/* C. Activity End Date & Time (DatePicker or NA, mandatory) */}
                            <Col
                              xs={24}
                              xl={12}
                              span={24}
                              md={24}
                              sm={24}
                              style={{ position: 'relative' }}
                            >
                              <Radio.Group
                                size="small"
                                buttonStyle="solid"
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  right: 12,
                                  zIndex: 1
                                }}
                                value={
                                  isNAField(index, 'activity_end_time_date')
                                    ? 'na'
                                    : 'date'
                                }
                                disabled={fieldsLocked}
                                onChange={(e) =>
                                  setNAField(
                                    index,
                                    'activity_end_time_date',
                                    e.target.value === 'na'
                                  )
                                }
                              >
                                <Radio.Button value="date">Date</Radio.Button>
                                <Radio.Button value="na">NA</Radio.Button>
                              </Radio.Group>
                              <FormItem
                                {...restField}
                                label="Activity End Date & Time"
                                name={[name, 'activity_end_time_date']}
                                rules={[
                                  {
                                    required: true,
                                    message: 'Select Activity End Date & Time'
                                  }
                                ]}
                                className={styles.customLable}
                              >
                                {isNAField(index, 'activity_end_time_date') ? (
                                  <Input
                                    disabled
                                    style={{ width: '100%' }}
                                    placeholder="NA"
                                  />
                                ) : (
                                  <DatePicker
                                    showTime
                                    disabled={fieldsLocked}
                                    style={{ width: '100%' }}
                                    placeholder="Select Activity End Date & Time"
                                  />
                                )}
                              </FormItem>
                            </Col>
                            <Col hidden>
                              <FormItem
                                {...restField}
                                name={[name, 'activity_end_time_user_id']}
                              >
                                <Input />
                              </FormItem>
                            </Col>

                            {/* D. LAF Off Time — last activity: DatePicker or NA; others: legacy N/A */}
                            {index < total - 1 ? (
                              <Col xs={24} xl={12} span={24} md={24} sm={24}>
                                <FormItem
                                  {...restField}
                                  label="LAF Off Time"
                                  name={[name, 'laf_off_time']}
                                  className={styles.customLable}
                                >
                                  <Input disabled style={{ width: '100%' }} />
                                </FormItem>
                              </Col>
                            ) : (
                              <Col
                                xs={24}
                                xl={12}
                                span={24}
                                md={24}
                                sm={24}
                                style={{ position: 'relative' }}
                              >
                                <Radio.Group
                                  size="small"
                                  buttonStyle="solid"
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 12,
                                    zIndex: 1
                                  }}
                                  value={
                                    isNAField(index, 'laf_off_time')
                                      ? 'na'
                                      : 'date'
                                  }
                                  disabled={fieldsLocked}
                                  onChange={(e) =>
                                    setNAField(
                                      index,
                                      'laf_off_time',
                                      e.target.value === 'na'
                                    )
                                  }
                                >
                                  <Radio.Button value="date">Date</Radio.Button>
                                  <Radio.Button value="na">NA</Radio.Button>
                                </Radio.Group>
                                <FormItem
                                  {...restField}
                                  label="LAF Off Time"
                                  name={[name, 'laf_off_time']}
                                  rules={[
                                    {
                                      required: true,
                                      message: 'Select LAF Off Time'
                                    }
                                  ]}
                                  className={styles.customLable}
                                >
                                  {isNAField(index, 'laf_off_time') ? (
                                    <Input
                                      disabled
                                      style={{ width: '100%' }}
                                      placeholder="NA"
                                    />
                                  ) : (
                                    <DatePicker
                                      showTime
                                      disabled={fieldsLocked}
                                      style={{ width: '100%' }}
                                      placeholder="Select LAF Off Time"
                                    />
                                  )}
                                </FormItem>
                              </Col>
                            )}

                            {/* E. Type of Cleaning */}
                            <Col xs={24} xl={12} span={12} md={24} sm={24}>
                              <FormItem
                                {...restField}
                                label="Type of Cleaning"
                                name={[name, 'type_of_cleaning']}
                                rules={[
                                  {
                                    required: true,
                                    message: 'Select Type of Cleaning'
                                  }
                                ]}
                                className={styles.customLable}
                              >
                                <Select
                                  placeholder="Select Type of Cleaning"
                                  disabled={fieldsLocked}
                                >
                                  {typeOfCleaningOptions.map((item, idx) => (
                                    <Select.Option value={item.value} key={idx}>
                                      {item.label}
                                    </Select.Option>
                                  ))}
                                </Select>
                              </FormItem>
                            </Col>

                            {/* F. Start Date & Time */}
                            <Col xs={24} xl={12} span={24} md={24} sm={24}>
                              <FormItem
                                {...restField}
                                label="Start Date & Time"
                                name={[name, 'start_date']}
                                rules={[
                                  {
                                    required: true,
                                    message: 'Select Start Date'
                                  }
                                ]}
                                className={styles.customLable}
                              >
                                <DatePicker
                                  showTime
                                  disabled={fieldsLocked}
                                  style={{ width: '100%' }}
                                />
                              </FormItem>
                            </Col>

                            {/* G. End Date & Time */}
                            <Col xs={24} xl={12} span={24} md={24} sm={24}>
                              <FormItem
                                {...restField}
                                label="End Date & Time"
                                name={[name, 'end_date']}
                                rules={[
                                  {
                                    required: true,
                                    message: 'Select End Date'
                                  }
                                ]}
                                className={styles.customLable}
                              >
                                <DatePicker
                                  showTime
                                  disabled={fieldsLocked}
                                  style={{ width: '100%' }}
                                />
                              </FormItem>
                            </Col>

                            {/* H. Cleaning Accessories (YES / N/A) */}
                            <Col xs={24} xl={12} span={24} md={24} sm={24}>
                              <FormItem
                                {...restField}
                                label="Cleaning Accessories"
                                name={[name, 'cleaning_accessories']}
                                rules={[
                                  {
                                    required: true,
                                    message: 'Select Cleaning Accessories'
                                  }
                                ]}
                                className={styles.customLable}
                              >
                                <Select
                                  placeholder="Select Cleaning Accessories"
                                  disabled={fieldsLocked}
                                  style={{ width: '100%' }}
                                >
                                  {cleaningAccessoriesOptions.map(
                                    (opt, idx) => (
                                      <Select.Option
                                        value={opt.value}
                                        key={idx}
                                      >
                                        {opt.label}
                                      </Select.Option>
                                    )
                                  )}
                                </Select>
                              </FormItem>
                            </Col>

                            {/* I. Done By User Id & Date */}
                            <Col hidden>
                              <FormItem
                                {...restField}
                                name={[name, 'done_by_user_id']}
                              >
                                <Input />
                              </FormItem>
                            </Col>
                            <Col xs={24} xl={12} span={24} md={24} sm={24}>
                              <FormItem
                                {...restField}
                                label="Done By User Id & Date"
                                name={[
                                  name,
                                  'done_by_user_id_and_date_display'
                                ]}
                                className={styles.customLable}
                              >
                                <Input disabled style={{ width: '100%' }} />
                              </FormItem>
                            </Col>

                            {/* J. Verified By User Id & Date */}
                            <Col hidden>
                              <FormItem
                                {...restField}
                                name={[name, 'verified_by']}
                              >
                                <Input />
                              </FormItem>
                            </Col>
                            <Col xs={24} xl={12} span={24} md={24} sm={24}>
                              <FormItem
                                {...restField}
                                label="Verified By User Id & Date"
                                name={[name, 'verified_by_and_date_display']}
                                className={styles.customLable}
                              >
                                <Input disabled style={{ width: '100%' }} />
                              </FormItem>
                            </Col>

                            {/* K. Remarks (mandatory) */}
                            <Col xs={24} xl={12} span={24} md={24} sm={24}>
                              <FormItem
                                {...restField}
                                label="Remarks"
                                name={[name, 'remarks']}
                                rules={[
                                  {
                                    required: true,
                                    message: 'Enter Remarks'
                                  }
                                ]}
                                className={styles.customLable}
                              >
                                <Input.TextArea
                                  placeholder="Enter Remarks"
                                  style={{ width: '100%' }}
                                  disabled={fieldsLocked}
                                />
                              </FormItem>
                            </Col>

                            {/* Hidden: activity id for edit */}
                            <Col hidden>
                              <FormItem {...restField} name={[name, 'id']}>
                                <Input />
                              </FormItem>
                            </Col>
                          </Row>

                          {showVerifyButton && (
                            <div style={{ textAlign: 'center', marginTop: 16 }}>
                              <Space>
                                <CustomButton
                                  className={styles.inwardButton}
                                  type="primary"
                                  ghost
                                  loading={savingDraftIndex === index}
                                  disabled={savingDraftIndex !== null}
                                  onClick={() => handleSaveDraft(index)}
                                >
                                  Save Draft
                                </CustomButton>
                                <CustomButton
                                  className={styles.inwardButton}
                                  type="primary"
                                  disabled={savingDraftIndex !== null}
                                  onClick={() => handleVerifyActivity(index)}
                                >
                                  Initiate Verification for Activity {index + 1}
                                </CustomButton>
                              </Space>
                            </div>
                          )}
                        </Card>
                        <br />
                      </div>
                    );
                  })}

                  <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                    <CustomButton
                      type="dashed"
                      onClick={() => {
                        const currentTotal = (
                          form.getFieldValue('cleaning_activities') || []
                        ).length;
                        handleActivityCountChange('add', currentTotal + 1);
                        setActivityStates((prev) => [
                          ...prev,
                          {
                            isNew: true,
                            isVerified: false,
                            uniqueId: uuidv4()
                          }
                        ]);
                        add(getNewActivityDefaults());
                      }}
                      icon={<PlusCircleOutlined />}
                    >
                      Add Activity Log
                    </CustomButton>
                  </div>
                </>
              )}
            </Form.List>

            {/* ========== ACTION BUTTONS ========== */}
            <FormItem className="text-center">
              <Space>
                <CustomButton
                  className={styles.inwardButton}
                  htmlType="submit"
                  type="primary"
                  loading={isSaving}
                  disabled={!canSubmit || isSaving}
                >
                  {id ? 'Update' : 'Create'}
                </CustomButton>
                <CustomButton
                  className={styles.inwardButton}
                  type="primary"
                  ghost
                  onClick={() => history.back()}
                >
                  Back
                </CustomButton>
              </Space>
            </FormItem>

            {/* ========== VERIFICATION MODAL ========== */}
            {isModal ? (
              <CustomModal
                title="WH Verification"
                visible={isModal}
                width="50%"
                footer={null}
                className={styles.customModal}
                closable={false}
              >
                <div className={`${styles.table_row}`}>
                  <Form form={form2} layout="vertical" onFinish={onFinishModal}>
                    <Card
                      title={`Initiate Verification — Activity ${
                        (verifyingActivityIndex ?? 0) + 1
                      }`}
                      className={styles.CustomPanel}
                    >
                      <Row gutter={24}>
                        <Col xs={24} xl={12} span={24} md={24} sm={24}>
                          <FormItem
                            label="Verified By (User id)"
                            name="email"
                            rules={[
                              {
                                required: true,
                                message: 'Enter Verifier User Id'
                              }
                            ]}
                            className={styles.customLable}
                          >
                            <Input placeholder="Enter Verifier User Id" />
                          </FormItem>
                        </Col>
                        <Col xs={24} xl={12} span={24} md={24} sm={24}>
                          <FormItem
                            label="Password"
                            name="password"
                            rules={[
                              {
                                required: true,
                                message: 'Enter Password'
                              }
                            ]}
                            className={styles.customLable}
                          >
                            <Input.Password placeholder="Enter Password" />
                          </FormItem>
                        </Col>
                      </Row>
                    </Card>

                    <br />
                    <FormItem className="text-center">
                      <Space>
                        <CustomButton
                          className={styles.inwardButton}
                          htmlType="submit"
                          type="primary"
                          size="medium"
                          loading={isVerifying}
                          disabled={isVerifying}
                        >
                          Submit
                        </CustomButton>
                        <CustomButton
                          className={styles.inwardButton}
                          onClick={handleCloseModal}
                          type="primary"
                          ghost
                          size="medium"
                        >
                          Close
                        </CustomButton>
                      </Space>
                    </FormItem>
                  </Form>
                </div>
              </CustomModal>
            ) : (
              ''
            )}
          </Form>
        </Spin>
      </Layout>
    </Space>
  );
};
export default createProductNewPage;
